import React, { useMemo } from 'react'
import TextField from 'core/components/validatedForm/TextField'
import { customValidator, requiredValidator } from 'core/utils/fieldValidators'
import { SSO_ATTRIBUTE_PLACEHOLDER_REGEX } from '../attributes/helpers'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { isOpenCloud } from 'core/utils/helpers'

const USERNAME_ATTRIBUTE_MAPPING_HINT = `The template defines how to construct a username using attributes provided by 
the Identity Provider. You can define the username format using valid SAML attribute placeholders, for example: 
{username} or {firstName}.{lastName}. Placeholders correspond to the id attributes in the Default Attribute Map XML.`

const EMAIL_ATTRIBUTE_MAPPING_HINT = `The template defines how to construct an email address using attributes provided by 
the Identity Provider. You can define the email format using valid SAML attribute placeholders, for example: 
{email}. Placeholders correspond to the id attributes in the Default Attribute Map XML.`

const attributeMappingTemplateValidator = customValidator((value: string) => {
  if (!value || !value.trim()) return false
  const matches = value.match(SSO_ATTRIBUTE_PLACEHOLDER_REGEX) || []
  if (matches.length === 0) return false
  // Ensure there are no stray braces outside of placeholders
  const residual = value.replace(SSO_ATTRIBUTE_PLACEHOLDER_REGEX, '')
  if (residual.includes('{') || residual.includes('}')) return false
  return true
}, 'Template must contain one or more placeholders like {attribute}, with any separators between them.')

interface Props {
  values: any
  setFieldValue: any
  groups: any[]
  groupId?: string
  ssoAttributeKeys?: string[]
}

const useStyles = makeStyles<Theme>(() => ({
  tooltipWide: {
    maxWidth: '500px',
  },
}))

const GroupSettingsFields = ({
  values,
  setFieldValue,
  groups,
  groupId,
  ssoAttributeKeys,
}: Props) => {
  const classes = useStyles()
  const nameValidator = useMemo(() => {
    return customValidator((name: string) => {
      return !groups.some((group) => {
        return groupId ? group.id !== groupId && group.name === name : group.name === name
      })
    }, `Another group already has this name.`)
  }, [groups, groupId])

  const attributeKeyValidator = useMemo(() => {
    return customValidator((value: string) => {
      const matches: string[] = value.match(SSO_ATTRIBUTE_PLACEHOLDER_REGEX) ?? []
      if (matches.length === 0) return false
      return matches.every((m) => {
        const attr = m.slice(1, -1)
        return ssoAttributeKeys?.includes(attr)
      })
    }, 'All attributes must be valid SAML attributes')
  }, [ssoAttributeKeys])

  const attributeMappingTemplateValidations = [
    requiredValidator,
    attributeMappingTemplateValidator,
    attributeKeyValidator,
  ]

  // Omit attributeKeyValidator for PCD because the attributes API may be unreachable.
  // That API requires CORS to be enabled on the infra region to allow cross‑region requests.
  // Without CORS, validation could fail even for valid templates, so we skip it here.
  const pcdAttributeMappingTemplateValidations = [
    requiredValidator,
    attributeMappingTemplateValidator,
  ]

  return (
    <>
      <TextField
        id="name"
        label="Name"
        onChange={setFieldValue('name')}
        value={values.name}
        validations={[nameValidator]}
        required
      />
      <TextField
        id="description"
        label="Description"
        onChange={setFieldValue('description')}
        value={values.description}
        required
      />
      <TextField
        id="usernameAttributeMappingTemplate"
        label="Username Attribute Mapping Template"
        onChange={setFieldValue('usernameAttributeMapping')}
        value={values.usernameAttributeMapping}
        placeholder="E.g {firstName} {lastName}"
        info={USERNAME_ATTRIBUTE_MAPPING_HINT}
        tooltipClassName={classes.tooltipWide}
        validations={
          isOpenCloud()
            ? attributeMappingTemplateValidations
            : pcdAttributeMappingTemplateValidations
        }
        required
      />
      <TextField
        id="emailAttributeMappingTemplate"
        label="Email Attribute Mapping Template"
        onChange={setFieldValue('emailAttributeMapping')}
        value={values.emailAttributeMapping}
        placeholder="E.g {email}"
        info={EMAIL_ATTRIBUTE_MAPPING_HINT}
        validations={
          isOpenCloud()
            ? attributeMappingTemplateValidations
            : pcdAttributeMappingTemplateValidations
        }
        required
      />
    </>
  )
}

export default GroupSettingsFields
